import { type SVGProps, useState } from 'react'
import { useSelector } from 'react-redux'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { Box, Typography } from '@mui/material'

import CardTypeChip from '../CardTypeChip'

import { FeatureCardList } from './FeatureCardList'

import { CardActivityType } from '@/models/card-activity/card-activity.model'
import { useAuthUser, useProfileUser } from '@/hooks/useUser'
import { getDefaultSet } from '@/services/set/create-set.service'
import { useCreateSet } from '@/views/NewBuilder/hooks/set/useCreateSet'

export interface FeatureOption {
  id: CardActivityType | 'BULK_CREATE'
  value: CardActivityType | 'BULK_CREATE'
  title: string
  icon: JSX.Element
  bgIcon: JSX.Element
  color: string
  tag?: JSX.Element
}

export type CreationMode = 'single' | 'bulk'

const features: FeatureOption[] = [
  {
    id: CardActivityType.READ_RESPOND,
    value: CardActivityType.READ_RESPOND,
    title: 'Spoken Open Response',
    icon: <CardTypeChip iconOnly size="large" type={CardActivityType.READ_RESPOND} />,
    color: '#528BFF',
    bgIcon: <SpokenOpenResponseBgIcon />,
  },
  // {
  //   id: CardActivityType.RESPOND_WRITE,
  //   value: CardActivityType.RESPOND_WRITE,
  //   title: 'Written Open Response',
  //   icon: <CardTypeChip iconOnly size="large" type={CardActivityType.RESPOND_WRITE} />,
  //   color: 'rgb(134, 203, 60)',
  //   bgIcon: <WrittenOpenResponseBgIcon />,
  // },
  {
    id: CardActivityType.SHORT_ANSWER,
    value: CardActivityType.SHORT_ANSWER,
    title: 'Short Answer',
    icon: <CardTypeChip iconOnly size="large" type={CardActivityType.SHORT_ANSWER} />,
    color: '#3CCB7F',
    bgIcon: <ShortAnswerBgIcon />,
  },
  {
    id: CardActivityType.REPEAT,
    value: CardActivityType.REPEAT,
    title: 'Listen & Repeat',
    icon: <CardTypeChip iconOnly size="large" type={CardActivityType.REPEAT} />,
    bgIcon: <RepeatBgIcon />,
    color: '#2CE',
  },
  {
    id: 'BULK_CREATE',
    value: 'BULK_CREATE',
    title: 'Bulk Create',
    icon: <BulkCreateIcon />,
    color: '#FDB022',
    bgIcon: <BulkCreateBgIcon />,
    tag: (
      <Box bgcolor="#FDF2FA" border="1px solid #FCCEEE" borderRadius="100px" padding="2px 6px">
        <Typography color="#C11574" fontSize="12px" fontWeight={500} lineHeight="16px">
          New!
        </Typography>
      </Box>
    ),
  },
]

function FeatureSelector() {
  const [selectedFeature, setSelectedFeature] = useState<FeatureOption | null>(null)
  const [creatingSet, setCreatingSet] = useState(false)

  const profile = useProfileUser()
  const auth = useAuthUser()
  const orgId = useSelector(({ organization }) => organization?.id)
  const { mutationCreateSet } = useCreateSet()
  const navigate = useNavigate()

  const handleCardClick = (feature: FeatureOption) => {
    setSelectedFeature(feature)
    if (feature.id === 'BULK_CREATE') {
      handleCreateSet('bulk', null)
    } else {
      handleCreateSet('single', feature.value as CardActivityType)
    }
  }

  const handleCreateSet = async (creationMode: CreationMode, cardType: CardActivityType | null) => {
    if (creatingSet) return
    setCreatingSet(true)
    try {
      const setToCreate = getDefaultSet({
        userId: auth.uid,
        displayName: profile?.displayName,
        defaultAssignmentRepeat: profile?.defaultAssignmentRepeat,
        nativeLanguage: profile?.nativeLanguage,
        defaultLanguage: profile?.nativeLanguage || 'en',
        defaultRubricId: profile?.defaultRubricId || null,
        additionalLanguages: profile?.additionalLanguages || [],
        repeat: profile?.defaultAssignmentRepeat || 1,
        language: profile?.language,
        organizationId: orgId,
        subjects: profile?.subjects || [],
      })

      const setId = await mutationCreateSet.mutateAsync({ data: setToCreate })

      const params = createSearchParams({
        ...(cardType && { cardType: cardType }),
        mode: creationMode,
      })

      navigate({
        pathname: `/b/${setId}`,
        search: `?${params}`,
      })
    } catch (error) {
      console.log(error)
    } finally {
      setCreatingSet(false)
    }
  }

  return (
    <FeatureCardList
      creatingSet={creatingSet}
      features={features}
      selectedFeatureId={selectedFeature?.id}
      variant="horizontal"
      onCardClick={handleCardClick}
    />
  )
}

export default FeatureSelector

export function BulkCreateIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={32} viewBox="0 0 32 32" width={32} xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect fill="#DC6803" height={31.2} rx={15.6} width={31.2} x={0.400391} y={0.400391} />
      <g clipPath="url(#clip0_3585_12428)">
        <path
          d="M12.752 14.3747L9.50195 15.9997L15.7694 19.1334C15.8547 19.1761 15.8973 19.1974 15.942 19.2058C15.9816 19.2132 16.0223 19.2132 16.0619 19.2058C16.1066 19.1974 16.1492 19.1761 16.2345 19.1334L22.502 15.9997L19.252 14.3747M12.752 17.6247L9.50195 19.2497L15.7694 22.3834C15.8547 22.4261 15.8973 22.4474 15.942 22.4558C15.9816 22.4632 16.0223 22.4632 16.0619 22.4558C16.1066 22.4474 16.1492 22.4261 16.2345 22.3834L22.502 19.2497L19.252 17.6247M9.50195 12.7497L15.7694 9.616C15.8547 9.57337 15.8973 9.55205 15.942 9.54366C15.9816 9.53623 16.0223 9.53623 16.0619 9.54366C16.1066 9.55205 16.1492 9.57337 16.2345 9.616L22.502 12.7497L16.2345 15.8834C16.1492 15.9261 16.1066 15.9474 16.0619 15.9558C16.0223 15.9632 15.9816 15.9632 15.942 15.9558C15.8973 15.9474 15.8547 15.9261 15.7694 15.8834L9.50195 12.7497Z"
          stroke="white"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.3}
        />
      </g>
      <defs>
        <clipPath id="clip0_3585_12428">
          <rect fill="white" height={15.6} transform="translate(8.20117 8.2002)" width={15.6} />
        </clipPath>
      </defs>
    </svg>
  )
}

export function SpokenOpenResponseBgIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={101} viewBox="0 0 100 101" width={100} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g filter="url(#filter0_f_3580_11892)">
        <rect
          fill="#1347D1"
          height={10.3713}
          rx={5.18566}
          stroke="#1347D1"
          strokeWidth={3.05882}
          width={10.3713}
          x={6.47059}
          y={53.3143}
        />
        <rect
          fill="#1347D1"
          height={54.2463}
          rx={5.18566}
          stroke="#1347D1"
          strokeWidth={3.05882}
          width={10.3713}
          x={31.371}
          y={31.3768}
        />
        <rect
          fill="#1347D1"
          height={54.2463}
          rx={5.18566}
          stroke="#1347D1"
          strokeWidth={3.05882}
          width={10.3713}
          x={56.2714}
          y={31.3768}
        />
        <rect
          fill="#1347D1"
          height={39.6213}
          rx={5.18566}
          stroke="#1347D1"
          strokeWidth={3.05882}
          width={10.3713}
          x={81.1737}
          y={38.6893}
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height={125}
          id="filter0_f_3580_11892"
          width={292.279}
          x={0.941406}
          y={-4}
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3580_11892" stdDeviation={2} />
        </filter>
      </defs>
    </svg>
  )
}

export function WrittenOpenResponseBgIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={113} viewBox="0 0 156 113" width={156} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g filter="url(#filter0_f_3580_11953)">
        <path
          d="M280.158 150.44C277.163 150.425 274.19 149.417 271.829 147.38C266.644 142.907 266.372 135.164 271.221 130.09C281.809 119.004 296.37 100.016 305.482 79.9696C312.016 65.5913 317.941 45.7291 309.059 32.7619C308.982 32.6516 308.891 32.55 308.795 32.4726C304.066 28.5818 294.892 28.3402 287.439 31.9039C274.975 38.0104 267.161 50.452 258.887 63.6234C253.737 71.8221 248.412 80.2992 241.614 87.8697L241.573 87.9222C230.958 101.278 209.989 109.885 194.645 100.478C188.223 96.5412 183.987 90.0696 183.018 82.7218C182.354 77.7016 183.106 72.9676 183.77 68.7894C184.113 66.6344 184.439 64.5981 184.489 62.9429C184.493 62.8396 184.497 62.7363 184.503 62.6352C184.956 54.7933 178.236 50.8661 176.165 49.8488C174.092 48.8293 166.816 45.8821 160.153 50.6765C160.068 50.7377 159.98 50.7988 159.892 50.8577C155.383 53.9438 152.092 59.3553 148.284 65.6195C145.831 69.6524 143.296 73.8256 140.114 77.8725C132.593 87.4399 120.495 94.144 109.298 94.9519C100.878 95.5583 93.2317 92.8752 87.7693 87.4C82.5517 82.1701 80.7316 75.8359 79.2698 70.7479C78.76 68.9756 78.2788 67.3023 77.7445 66.0135C77.5007 65.4231 76.9325 65.0048 76.3366 64.9688C76.2307 64.9639 76.1201 64.9611 76.0094 64.9606C70.8612 64.9348 60.532 70.8673 47.7658 87.8481C36.6932 102.573 28.3793 119.501 26.177 127.955C24.4545 134.561 17.4816 138.747 10.591 137.299C3.70496 135.851 -0.480806 129.32 1.24177 122.712C4.63387 109.685 15.2354 89.6396 27.0204 73.9641C34.2696 64.3249 41.6698 56.6256 49.0195 51.0804C59.0411 43.5195 69.0004 39.9639 78.6192 40.5134C88.8641 41.0967 97.921 47.3462 101.689 56.4382C102.723 58.9321 103.44 61.4288 104.075 63.6326C104.802 66.1601 105.704 69.3063 106.643 70.2475C106.777 70.3801 106.995 70.601 108.109 70.5209C111.746 70.2577 116.84 67.1966 119.707 63.5526C121.941 60.7102 123.981 57.3568 126.14 53.806C130.797 46.1457 136.074 37.4661 145.187 31.1846C157.705 22.2511 174.276 21.0283 188.465 28.0004C202.656 34.9725 210.963 48.4161 210.178 63.1112C210.069 66.1951 209.597 69.1497 209.182 71.7616C208.749 74.4855 208.303 77.3018 208.521 78.9671C208.565 79.2927 208.756 79.6102 208.995 79.7587C210.565 80.7229 217.377 78.3673 221.263 73.4798C221.625 73.0287 221.957 72.6346 222.292 72.2604C227.619 66.3248 232.144 59.1233 236.936 51.4966C246.334 36.5346 256.985 19.5783 276.298 10.1447C276.324 10.1316 276.352 10.1186 276.378 10.1055C293.421 1.92244 313.326 3.37424 325.908 13.7196C327.75 15.2326 329.363 16.9928 330.703 18.9474C342.59 36.3002 342.009 60.5751 329.02 89.1496C315.918 117.979 295.837 140.175 289.994 146.29C287.354 149.056 283.741 150.458 280.158 150.44Z"
          fill="#396B04"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height={153.605}
          id="filter0_f_3580_11953"
          width={346.354}
          x={-3.14453}
          y={0.834961}
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3580_11953" stdDeviation={2} />
        </filter>
      </defs>
    </svg>
  )
}

export function RepeatBgIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={119} viewBox="0 0 92 119" width={92} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_3580_11929)">
        <g filter="url(#filter0_f_3580_11929)">
          <path
            d="M76.5128 125.78C17.2292 129.255 -29.0434 54.8011 21.8257 12.4211C70.5264 -27.9047 146.419 39.4897 93.887 79.125C82.3276 85.3709 60.5197 86.5453 56.6797 70.0728C54.5289 60.8842 60.1194 49.4612 70.0161 48.0449C71.582 47.7766 78.712 49.4261 79.341 48.8674C82.0549 46.457 73.0863 37.3609 71.5512 36.2964C66.0926 32.5049 59.1297 31.1457 52.6551 32.5664C41.408 35.034 33.3279 43.3473 32.2195 54.8802C29.5232 82.9121 60.9023 96.2837 84.4741 93.5126C102.724 92.0523 118.391 79.1338 122.46 61.1526C124.162 54.9286 124.303 47.7282 129.018 42.837C137.331 34.2159 152.322 37.9855 155.664 49.4568C158.172 92.2678 118.453 127.474 76.5216 125.789L76.5128 125.78Z"
            fill="#20BCFF"
          />
        </g>
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height={133.896}
          id="filter0_f_3580_11929"
          width={163.777}
          x={-4}
          y={-3.99805}
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3580_11929" stdDeviation={2} />
        </filter>
        <clipPath id="clip0_3580_11929">
          <rect fill="white" height={125.899} width={155.77} />
        </clipPath>
      </defs>
    </svg>
  )
}

export function BulkCreateBgIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={128} viewBox="0 0 96 128" width={96} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_3585_12408)" filter="url(#filter0_f_3585_12408)">
        <path
          d="M99.8205 117.893C-41.4805 108.744 -5.40322 -122.416 133.795 -46.8481C202.479 -6.65826 187.901 118.205 99.8268 117.893H99.8205ZM53.3526 20.5635C51.5053 73.0911 131.404 98.06 132.784 33.3818C132.865 -16.662 56.8286 -41.1691 53.3526 20.5635Z"
          fill="#F79009"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height={199.894}
          id="filter0_f_3585_12408"
          width={187}
          x={0}
          y={-72}
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3585_12408" stdDeviation={5} />
        </filter>
        <clipPath id="clip0_3585_12408">
          <rect fill="white" height={179.893} transform="translate(10 -62)" width={167} />
        </clipPath>
      </defs>
    </svg>
  )
}

export function MultipleChoiceBgIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={128} viewBox="0 0 96 128" width={96} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_3585_12408)" filter="url(#filter0_f_3585_12408)">
        <path
          d="M99.8205 117.893C-41.4805 108.744 -5.40322 -122.416 133.795 -46.8481C202.479 -6.65826 187.901 118.205 99.8268 117.893H99.8205ZM53.3526 20.5635C51.5053 73.0911 131.404 98.06 132.784 33.3818C132.865 -16.662 56.8286 -41.1691 53.3526 20.5635Z"
          fill="#6938EF"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height={199.894}
          id="filter0_f_3585_12408"
          width={187}
          x={0}
          y={-72}
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_3585_12408" stdDeviation={5} />
        </filter>
        <clipPath id="clip0_3585_12408">
          <rect fill="white" height={179.893} transform="translate(10 -62)" width={167} />
        </clipPath>
      </defs>
    </svg>
  )
}

export function ShortAnswerBgIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={68} viewBox="0 0 343 68" width={343} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g filter="url(#filter0_f_4457_29862)">
        <path
          d="M74.148 151.802C77.8448 151.986 81.5297 151.166 84.476 149.267C90.946 145.096 91.4034 137.299 85.4976 131.855C72.6027 119.959 54.9264 99.8241 43.9932 78.9828C36.154 64.0344 29.1512 43.5907 40.3175 31.0917C40.4139 30.9854 40.5276 30.8889 40.6478 30.8172C46.5456 27.204 57.8722 27.57 67.0157 31.6629C82.3043 38.6557 91.755 51.7343 101.762 65.5802C107.989 74.1986 114.43 83.1098 122.702 91.2037L122.752 91.2595C135.645 105.447 161.392 115.53 180.478 107.054C188.467 103.507 193.797 97.2557 195.108 89.903C196.007 84.8795 195.151 80.0507 194.398 75.789C194.008 73.5908 193.638 71.5136 193.601 69.8394C193.598 69.7349 193.595 69.6303 193.589 69.5279C193.153 61.5818 201.509 58.0643 204.081 57.1751C206.655 56.2838 215.683 53.7925 223.832 59.0753C223.936 59.1426 224.043 59.2101 224.15 59.2754C229.668 62.6904 233.646 68.3719 238.248 74.9484C241.212 79.1824 244.277 83.5636 248.14 87.8602C257.274 98.018 272.102 105.59 285.909 107.15C296.293 108.322 305.773 106.122 312.6 100.958C319.122 96.0254 321.468 89.7524 323.352 84.7136C324.009 82.9584 324.629 81.3013 325.309 80.0359C325.619 79.4561 326.327 79.0716 327.063 79.0749C327.194 79.077 327.33 79.0815 327.467 79.0883C333.822 79.4046 346.478 86.0798 361.97 104.07C375.406 119.67 385.403 137.311 387.989 145.99C390.012 152.774 398.553 157.463 407.081 156.459C415.603 155.455 420.872 149.141 418.849 142.355C414.866 128.98 402.094 108.041 387.793 91.4339C378.996 81.2217 369.983 72.9576 360.998 66.8715C348.746 58.573 336.509 54.3216 324.628 54.2368C311.974 54.1445 300.697 59.8509 295.904 68.7783C294.588 71.2269 293.665 73.6995 292.846 75.8819C291.91 78.385 290.746 81.5008 289.573 82.3885C289.406 82.5135 289.133 82.722 287.759 82.567C283.274 82.0596 277.034 78.6308 273.552 74.7619C270.84 71.7441 268.374 68.2234 265.766 64.4956C260.137 56.4533 253.759 47.341 242.61 40.3943C227.299 30.5442 206.864 28.2081 189.241 34.3027C171.616 40.3972 161.152 53.4154 161.891 68.3014C161.978 71.4217 162.513 74.4355 162.985 77.0997C163.477 79.8782 163.984 82.7506 163.688 84.4172C163.629 84.743 163.388 85.0508 163.09 85.1848C161.137 86.0537 152.766 83.223 148.047 78.031C147.607 77.5516 147.203 77.1317 146.796 76.7317C140.313 70.3858 134.841 62.8155 129.045 54.7982C117.68 39.0702 104.799 21.2457 81.1079 10.4389C81.0762 10.424 81.0416 10.409 81.01 10.3941C60.1022 1.00068 35.5104 1.14281 19.819 10.7493C17.5206 12.1541 15.5027 13.8238 13.8178 15.7077C-1.12575 32.4341 -0.788221 56.9768 14.7966 86.6846C30.517 116.657 54.9555 140.398 62.0729 146.96C65.2872 149.927 69.7255 151.582 74.148 151.802Z"
          fill="#16B364"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height={158.27}
          id="filter0_f_4457_29862"
          width={421.548}
          x={0.293433}
          y={0.88523}
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
          <feGaussianBlur result="effect1_foregroundBlur_4457_29862" stdDeviation={1.28004} />
        </filter>
      </defs>
    </svg>
  )
}
